import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, X, Download, FileText, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import * as api from '../api';
import { toast } from 'sonner';
import { useSociete } from '../context/SocieteContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtEur(v) {
  return (v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}
function fmtDate(s) {
  if (!s) return '—';
  const d = s.split('T')[0].split('-');
  return `${d[2]}/${d[1]}/${d[0]}`;
}

const MODULE_CFG = {
  vente:    { label: 'Vente',    dot: '#3B82F6' },
  location: { label: 'Location', dot: '#10B981' },
  atelier:  { label: 'Atelier',  dot: '#F59E0B' },
};


const STATUT_PMT_CFG = {
  en_attente:          { label: 'En attente',          color: '#B45309' },
  validee:             { label: 'Validée',              color: '#1D4ED8' },
  partiellement_payee: { label: 'Part. payée',          color: '#B45309' },
  payee:               { label: 'Payée',                color: '#15803D' },
};

function ModuleDot({ module }) {
  const cfg = MODULE_CFG[module] || { label: module, dot: '#94A3B8' };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#374151' }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
}

function StatutCell({ statut_paiement, ext_status }) {
  const pmt = STATUT_PMT_CFG[statut_paiement];
  return (
    <span style={{ fontSize: 12, color: pmt?.color || '#6B7280' }}>
      {pmt?.label || statut_paiement || '—'}
      {ext_status === 'draft' && (
        <span style={{ marginLeft: 4, fontSize: 11, color: '#B45309' }}>(brouillon)</span>
      )}
    </span>
  );
}

const TH = { padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700,
             color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em',
             background: '#F8FAFC', borderBottom: '1px solid #E2E8F0', whiteSpace: 'nowrap' };
const TD = { padding: '10px 12px', fontSize: 13, borderBottom: '1px solid #F1F5F9', color: '#1E293B' };

const MODULES = [
  { key: '',         label: 'Tous' },
  { key: 'vente',    label: 'Vente' },
  { key: 'location', label: 'Location' },
  { key: 'atelier',  label: 'Atelier' },
];
const STATUTS = [
  { key: '',                    label: 'Tous statuts' },
  { key: 'en_attente',          label: 'En attente' },
  { key: 'validee',             label: 'Validée' },
  { key: 'partiellement_payee', label: 'Part. payée' },
  { key: 'payee',               label: 'Payée' },
];
const EXT_STATUTS = [
  { key: '',       label: 'Tous' },
  { key: 'draft',  label: 'Brouillon' },
  { key: 'posted', label: 'Validée PL' },
];

export default function FacturesClient() {
  const { activeAgences } = useSociete();

  const [rows, setRows]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(false);

  const [module,   setModule]   = useState('');
  const [statut,   setStatut]   = useState('');
  const [extSt,    setExtSt]    = useState('');
  const [dateD,    setDateD]    = useState('');
  const [dateF,    setDateF]    = useState('');
  const [search,   setSearch]   = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchTimer = useRef(null);

  const PER_PAGE = 50;

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, per_page: PER_PAGE };
      if (module)  params.module          = module;
      if (statut)  params.statut_paiement = statut;
      if (extSt)   params.ext_status      = extSt;
      if (dateD)   params.date_debut      = dateD;
      if (dateF)   params.date_fin        = dateF;
      if (search.trim()) params.q = search.trim();
      if (activeAgences?.length) params.agence_ids = activeAgences.map(a => a.agence_id).join(',');
      const res = await api.getFacturesUnifiees(params);
      setRows(res.data.data || []);
      setTotal(res.data.total || 0);
      setPage(p);
    } catch (e) {
      toast.error('Erreur chargement factures');
    } finally {
      setLoading(false);
    }
  }, [module, statut, extSt, dateD, dateF, search, activeAgences]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(1); }, [module, statut, extSt, dateD, dateF, search, activeAgences]); // eslint-disable-line react-hooks/exhaustive-deps


  const handlePdf = async (facture) => {
    try {
      const res = await api.getFactureUnifieePdf(facture.facture_id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${facture.ext_invoice_number || facture.facture_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error('PDF non disponible');
    }
  };

  const handleAcquittee = async (facture) => {
    try {
      const res = await api.getFactureAcquitteeBlob(facture.facture_id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Acquittee_${facture.ext_invoice_number || facture.facture_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error('Facture acquittée non disponible');
    }
  };

  const totalPages = Math.ceil(total / PER_PAGE);
  const canAcquittee = (f) =>
    f.statut_paiement === 'payee' && f.ext_status === 'posted' && f.ext_system === 'pennylane';

  return (
    <div style={{ padding: '24px' }}>
      {/* En-tête */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: '#0F172A', margin: 0 }}>Factures client</h1>
        <p style={{ fontSize: 13, color: '#64748B', margin: '4px 0 0' }}>
          {total.toLocaleString('fr-FR')} facture{total !== 1 ? 's' : ''} — Ventes · Location · Atelier
        </p>
      </div>

      {/* Filtres */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        {/* Module pills */}
        <div style={{ display: 'flex', gap: 4, background: '#F1F5F9', borderRadius: 8, padding: 3 }}>
          {MODULES.map(m => (
            <button key={m.key}
              data-testid={`filter-module-${m.key || 'tous'}`}
              onClick={() => setModule(m.key)}
              style={{
                padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                border: 'none', cursor: 'pointer', transition: 'all .15s',
                background: module === m.key ? '#fff' : 'transparent',
                color: module === m.key ? '#0F172A' : '#64748B',
                boxShadow: module === m.key ? '0 1px 3px rgba(0,0,0,.1)' : 'none',
              }}>
              {m.key && <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
                background: MODULE_CFG[m.key]?.dot, marginRight: 5, verticalAlign: 'middle' }} />}
              {m.label}
            </button>
          ))}
        </div>

        {/* Statut paiement */}
        <select value={statut} onChange={e => setStatut(e.target.value)}
          data-testid="filter-statut-paiement"
          style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #E2E8F0',
            fontSize: 12, color: '#374151', background: '#fff', cursor: 'pointer' }}>
          {STATUTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>

        {/* Statut PL */}
        <select value={extSt} onChange={e => setExtSt(e.target.value)}
          data-testid="filter-ext-status"
          style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #E2E8F0',
            fontSize: 12, color: '#374151', background: '#fff', cursor: 'pointer' }}>
          {EXT_STATUTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>

        {/* Dates */}
        <input type="date" value={dateD} onChange={e => setDateD(e.target.value)}
          data-testid="filter-date-debut"
          style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #E2E8F0', fontSize: 12, color: '#374151' }} />
        <input type="date" value={dateF} onChange={e => setDateF(e.target.value)}
          data-testid="filter-date-fin"
          style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #E2E8F0', fontSize: 12, color: '#374151' }} />

        {/* Recherche */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <Search size={13} style={{ position: 'absolute', left: 8, color: '#94A3B8', pointerEvents: 'none' }} />
          <input value={searchInput}
            onChange={e => {
              const v = e.target.value;
              setSearchInput(v);
              clearTimeout(searchTimer.current);
              searchTimer.current = setTimeout(() => setSearch(v), 300);
            }}
            placeholder="N° facture, client, document, montant…"
            data-testid="filter-search"
            style={{ paddingLeft: 28, paddingRight: searchInput ? 28 : 8, paddingTop: 5, paddingBottom: 5,
              borderRadius: 6, border: '1px solid #E2E8F0', fontSize: 12, width: 260 }} />
          {searchInput && (
            <button type="button"
              onClick={() => { clearTimeout(searchTimer.current); setSearchInput(''); setSearch(''); }}
              style={{ position: 'absolute', right: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 0, display: 'flex' }}>
              <X size={13} />
            </button>
          )}
        </div>

        {/* Reset */}
        {(module || statut || extSt || dateD || dateF || search) && (
          <button onClick={() => { setModule(''); setStatut(''); setExtSt(''); setDateD(''); setDateF(''); setSearch(''); setSearchInput(''); }}
            style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #E2E8F0',
              fontSize: 12, background: '#fff', cursor: 'pointer', color: '#64748B', display: 'flex', alignItems: 'center', gap: 4 }}>
            <X size={11} /> Réinitialiser
          </button>
        )}
      </div>

      {/* Tableau */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60, color: '#94A3B8' }}>
            <Loader2 size={20} style={{ animation: 'spin 1s linear infinite', marginRight: 8 }} />
            Chargement…
          </div>
        ) : rows.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94A3B8', fontSize: 13 }}>
            Aucune facture
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={TH}>N° Facture</th>
                  <th style={TH}>Client</th>
                  <th style={TH}>Document</th>
                  <th style={TH}>Module</th>
                  <th style={TH}>Statut</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Montant TTC</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Réglé</th>
                  <th style={TH}>Date</th>
                  <th style={TH}>Échéance</th>
                  <th style={{ ...TH, textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(f => (
                  <tr key={f.facture_id}
                    data-testid={`facture-row-${f.facture_id}`}
                    style={{ transition: 'background .1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={TD}>{f.ext_invoice_number}</td>
                    <td style={TD}>{f.entreprise_nom}</td>
                    <td style={{ ...TD, color: '#64748B', fontSize: 12 }}>{f.devis_numero || f.vente_id?.slice(0, 8) || '—'}</td>
                    <td style={TD}><ModuleDot module={f.module} /></td>
                    <td style={TD}><StatutCell statut_paiement={f.statut_paiement} ext_status={f.ext_status} /></td>
                    <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtEur(f.montant_ttc)}</td>
                    <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: f.montant_paye > 0 ? '#15803D' : '#94A3B8' }}>
                      {f.montant_paye > 0 ? fmtEur(f.montant_paye) : '—'}
                    </td>
                    <td style={TD}>{fmtDate(f.date_facture)}</td>
                    <td style={TD}>{fmtDate(f.date_echeance)}</td>
                    <td style={{ ...TD, textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                        {f.ext_system === 'pennylane' && (
                          <button onClick={() => handlePdf(f)}
                            title="Télécharger PDF"
                            data-testid={`btn-pdf-${f.facture_id}`}
                            style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #E2E8F0',
                              background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                              fontSize: 11, color: '#475569' }}>
                            <FileText size={12} /> PDF
                          </button>
                        )}
                        {canAcquittee(f) && (
                          <button onClick={() => handleAcquittee(f)}
                            title="Facture acquittée"
                            data-testid={`btn-acquittee-${f.facture_id}`}
                            style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #BBF7D0',
                              background: '#F0FDF4', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                              fontSize: 11, color: '#15803D' }}>
                            <Download size={12} /> Acquittée
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 16px', borderTop: '1px solid #F1F5F9', background: '#F8FAFC' }}>
            <span style={{ fontSize: 12, color: '#64748B' }}>
              Page {page} / {totalPages} — {total.toLocaleString('fr-FR')} résultats
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => load(page - 1)} disabled={page <= 1}
                data-testid="pagination-prev"
                style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #E2E8F0',
                  background: page <= 1 ? '#F1F5F9' : '#fff', cursor: page <= 1 ? 'default' : 'pointer',
                  color: page <= 1 ? '#CBD5E1' : '#374151', display: 'flex', alignItems: 'center' }}>
                <ChevronLeft size={14} />
              </button>
              <button onClick={() => load(page + 1)} disabled={page >= totalPages}
                data-testid="pagination-next"
                style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #E2E8F0',
                  background: page >= totalPages ? '#F1F5F9' : '#fff', cursor: page >= totalPages ? 'default' : 'pointer',
                  color: page >= totalPages ? '#CBD5E1' : '#374151', display: 'flex', alignItems: 'center' }}>
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
